<template>
  <div id="app" :class="['css-hdr', 'unselectable']">
    <table-layout full-height>
      <row>
        <cell :class="['editing-area-wrapper']" y-align="middle" x-align="center">
          <editing-area :images="images" />
        </cell>
        <cell :class="['controls-area-wrapper']">
          <controls-area
            @layer-add="addLayer"
            @layer-delete="deleteLayer"
            @layer-toggle-visibility="toggleLayerVisibility"
            @layer-select="selectLayer"
            ref="controls-area"
            :images="images" />
        </cell>
      </row>
    </table-layout>
    <import-pane v-if="$root.importPane" />
    <error :message="error"/>
    <img :src="originalFile" :id="originalFileId" :class="[originalFileId, 'pointer-events-none']" />
    <social-bar />
    <v-tour name="tour" :steps="tourSteps" :class="['css-hdr-tour']" :callbacks="tourCallbacks" />
    <layers-preview :images="images" @layer-select="selectLayer" v-if="images.length" />
  </div>
</template>
<script>
import EditingArea from './components/editing-area'
import ControlsArea from './components/controls-area'
import ImportPane from './components/import-pane'
import SocialBar from './components/social-bar'
import Error from './components/error'
import LayersPreview from './components/layers-preview'
import methods from './helpers/methods'
import mixBlendModes from './helpers/mix-blend-modes'
import tourSteps from './helpers/tour-steps'
import uuid from 'uuid/v1'
import events from './helpers/events'
import Cookies from 'universal-cookie'
import config from './helpers/config'

export default {
  name: 'App',
  components: {
    Error,
    LayersPreview,
    SocialBar,
    ImportPane,
    EditingArea,
    ControlsArea
  },
  mixins: [
    methods
  ],
  data () {
    return {
      name: this.$root.config.name,
      tourSteps,
      error: null,
      images: [],
      tick: 100,
      initialImageUrl: null,
      originalFile: null,
      originalFileId: 'original-file',
      tourCallbacks: {
        onStop: this.onTourStopCallback
      }
    }
  },
  created () {
    this.bind()
  },
  methods: {
    /**
     * Get the name of the tour finished cookie
     * @method getTourCookieName
     * @return {string}
     */
    getTourCookieName () {
      return `${this.name}-tour-cookie`
    },
    /**
     * Check if the tour finished cookie is set
     * @method getTourCookie
     * @return {string}
     */
    getTourCookie () {
      const cookies = new Cookies()
      return cookies.get(this.getTourCookieName())
    },
    /**
     * Finish the tour and set a one-month cookie
     * @method onTourStopCallback
     * @return {void}
     */
    onTourStopCallback () {
      const cookies = new Cookies()
      cookies.set(this.getTourCookieName(), true, {
        path: '/',
        expires: this.oneMonthFromNow()
      })
    },
    /**
     * Get the dimensions of the originally imported file
     * @method getOriginalFileDimensions
     * @return {object}
     */
    getOriginalFileDimensions () {
      const originalFile = document.getElementById(this.originalFileId)
      return {
        width: originalFile.offsetWidth,
        height: originalFile.offsetHeight
      }
    },
    /**
     * Bind to the custom events, emitted by the components
     * @method bind
     * @return {void}
     */
    bind () {
      const root = this.$root
      const tick = this.tick
      root.$on(events.filtersApply, data => {
        this.images[this.$root.selectedLayer].filters = data.filters
        this.images[this.$root.selectedLayer].filtersString = data.filtersString
        this.images[this.$root.selectedLayer].mixBlendMode = data.mixBlendMode
      })
      root.$on(events.dataImported, data => {
        root.touched = false
        this.error = null
        if (data.type === 'image') {
          this.initialImageUrl = data.data
          this.originalFile = this.initialImageUrl
          this.images = []
          setTimeout(() => {
            this.addLayer()
          }, tick)
        } else {
          this.initialImageUrl = data.data.cssHdr[0].url
          this.images = data.data.cssHdr
          setTimeout(() => {
            this.$refs['controls-area'].setDefaultZoom()
            this.selectLayer(data.data.cssHdr[0].index)
          }, tick)
        }
        root.importPane = false
        if (!this.getTourCookie()) {
          this.$tours['tour'].start()
        }
      })
      root.$on(events.error, error => {
        this.error = error.message
        console.error(`[${config.description} ${events.error}]`, error.message)
      })
    },
    /**
     * Add new layer from a template
     * @method addLayer
     * @return {void}
     */
    addLayer () {
      const originalFileDimensions = this.getOriginalFileDimensions()
      const image = {
        width: originalFileDimensions.width,
        height: originalFileDimensions.height,
        url: this.initialImageUrl,
        mixBlendMode: mixBlendModes[10],
        filtersString: null,
        hidden: false,
        index: uuid(),
        filters: {
          brightness: {
            min: 0,
            max: 500,
            value: 100,
            unit: '%',
            type: 'range',
            render: true,
            icon: 'brightness-6'
          },
          contrast: {
            min: 0,
            max: 500,
            value: 100,
            unit: '%',
            type: 'range',
            render: true,
            icon: 'contrast-circle'
          },
          sepia: {
            min: 0,
            max: 100,
            value: 0,
            unit: '%',
            type: 'range',
            render: true,
            icon: 'image-filter-vintage'
          },
          saturate: {
            min: 0,
            max: 500,
            value: 100,
            unit: '%',
            type: 'range',
            render: true,
            icon: 'theme-light-dark'
          },
          'hue-rotate': {
            min: 0,
            max: 360,
            value: 0,
            unit: 'deg',
            type: 'range',
            render: true,
            icon: 'rotate-right'
          },
          invert: {
            min: 0,
            max: 100,
            value: 0,
            unit: '%',
            type: 'range',
            render: true,
            icon: 'invert-colors'
          },
          opacity: {
            min: 0,
            max: 100,
            value: 100,
            unit: '%',
            type: 'range',
            render: true,
            icon: 'opacity'
          },
          grayscale: {
            min: 0,
            max: 100,
            value: 0,
            unit: '%',
            type: 'range',
            render: true,
            icon: 'image-filter-black-white'
          },
          blur: {
            min: 0,
            max: 100,
            value: 0,
            unit: 'px',
            type: 'range',
            render: false,
            icon: 'blur'
          }
        }
      }
      this.images.push(image)
      this.$nextTick(() => {
        this.selectLayer(image.index)
      })
    },
    /**
     * Delete layer by index
     * @method deleteLayer
     * @params {number} index
     * @return {void}
     */
    deleteLayer (index = 0) {
      let currentIndex = this.findImageByIndex(index)
      this.images.splice(currentIndex, 1)
      this.$nextTick(() => {
        const length = this.images.length
        if (currentIndex === length) {
          this.selectLayer(this.images[length - 1].index)
        }
      })
    },
    /**
     * Toggle the visibility of a layer by index
     * @method toggleLayerVisibility
     * @params {number} index
     * @return {void}
     */
    toggleLayerVisibility (index = 0) {
      const item = this.findImageByIndex(index)
      this.images[item].hidden = !this.images[item].hidden
    },
    /**
     * Select layer by index
     * @method selectLayer
     * @params {number} index
     * @return {void}
     */
    selectLayer (index = 0) {
      const selectedLayerIndex = this.findImageByIndex(index)
      this.$root.selectedLayer = selectedLayerIndex
      this.$root.selectedImage = this.images[selectedLayerIndex]
    }
  }
}
</script>
<style>
:root
{
  --dark: #535353;
  --darker: #282828;
  --darkest: #000;
  --white: #f2f2f2;
  --em-0: .2em;
  --em-1: .25em;
  --em-2: .5em;
  --em-3: .75em;
  --em-4: 1em;
  --em-5: 1.25em;
  --em-6: 1.5em;
  --em-7: 1.75em;
  --em-8: 2em;
  --font-family: Roboto, Helvetica, sans-serif;
  --ease-in-out-back: all .5s cubic-bezier(0.680, -0.550, 0.265, 1.550);
  --pointer: pointer;
  --border-radius: 2px;
  --green: rgba(56, 142, 60, 1);
  --red: #C62828;
}

.m-1
{
  margin: var(--em-1);
}

.m-2
{
  margin: var(--em-2);
}

.ml-0
{
  margin-left: 0;
}

.mr-0
{
  margin-right: 0;
}

.my-1
{
  margin-top: var(--em-1);
  margin-bottom: var(--em-1);
}

.my-2
{
  margin-top: var(--em-2);
  margin-bottom: var(--em-2);
}

.my-3
{
  margin-top: var(--em-3);
  margin-bottom: var(--em-3);
}

.my-4
{
  margin-top: var(--em-4);
  margin-bottom: var(--em-4);
}

.mt-1
{
  margin-top: var(--em-1);
}

.mt-2
{
  margin-top: var(--em-2);
}

.mt-3
{
  margin-top: var(--em-3);
}

.mt-4
{
  margin-top: var(--em-4);
}

.mb-2
{
  margin-bottom: var(--em-2);
}

.px-0
{
  padding-left: 0;
  padding-right: 0;
}

.p-1
{
  padding: var(--em-1);
}

.p-2
{
  padding: var(--em-2);
}

.p-4
{
  padding: var(--em-4);
}

.py-2
{
  padding-top: var(--em-2);
  padding-bottom: var(--em-2);
}

.inline-block
{
  display: inline-block;
}

.unselectable
{
  user-select: none;
}

.flex
{
  display: flex;
}

.flex.equal-width > *
{
  flex-grow: 1;
}

.flex-wrap
{
  flex-wrap: wrap;
}

.flex-wrap > *
{
  flex-basis: 50%;
}

button,
.button,
.css-hdr-tour .v-step__button
{
  outline: 0 !important;
  border: 0 !important;
  background: var(--darker) !important;
  padding: var(--em-1) !important;
  text-align: center !important;
  cursor: var(--pointer) !important;
  border-radius: var(--border-radius) !important;
  opacity: .9 !important;
  transition: var(--ease-in-out-back) !important;
}

button *,
.button *
{
  display: inline-block;
  vertical-align: middle;
}

button span,
.button span
{
  color: var(--white);
  text-transform: uppercase;
  margin: 0 var(--em-2);
  font-weight: 400;
}

button:hover,
.button:hover,
.css-hdr-tour .v-step__button:hover
{
  opacity: 1 !important;
  background: var(--darker) !important;
  color: var(--white) !important;
}

button i,
.button i
{
  color: var(--white);
}

.block
{
  display: block;
}

html,
body
{
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: --font-family;
}

body
{
  background: var(--darker);
  color: var(--white)
}

*
{
  padding: 0;
  margin: 0;
  list-style: none;
  outline: 0;
  box-sizing: border-box;
}

.css-hdr
{
  font-family: Roboto, sans-serif;
  cursor: default;
}

.controls-area-wrapper
{
  width: 320px;
}

:disabled,
[disabled]
{
  pointer-events: none;
  opacity: .5;
}

input[type=range]
{
  -webkit-appearance: none;
  width: 100%;
}

input[type=range]:focus
{
  outline: none;
}

input[type=range]::-webkit-slider-runnable-track
{
  width: 100%;
  height: var(--em-1);
  cursor: pointer;
  background: var(--darker);
}

input[type=range]::-webkit-slider-thumb
{
  height: var(--em-8);
  width: var(--em-3);
  background: var(--darker);
  cursor: pointer;
  margin-top: -.8em;
  -webkit-appearance: none;
}

input[type=range]:focus::-webkit-slider-thumb,
input[type=range]:focus::-webkit-slider-runnable-track
{
  background: var(--white);
}

select,
button,
.button
.css-hdr-tour .v-step__button
{
  filter: drop-shadow(0 0 var(--em-0) var(--darkest));
}

select
{
  padding: var(--em-1);
  border-radius: 0;
  display: block;
  font-size: var(--em-4);
  font-family: var(--font-family);
  line-height: var(--em-4);
  color: var(--white);
  width: 100%;
  max-width: 100%;
  border: 1px solid #aaa;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23f2f2f2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E'),
  linear-gradient(to bottom, var(--darker) 0%,var(--darker) 100%);
  background-repeat: no-repeat, repeat;
  background-position: right .7em top 50%, 0 0;
  background-size: .65em auto, 100%;
}

.select-css:hover
{
  border-color: #888;
}

.select-css:focus
{
  border-color: #aaa;
  box-shadow: 0 0 1px 3px rgba(59, 153, 252, .7);
  box-shadow: 0 0 0 3px -moz-mac-focusring;
  color: #222;
  outline: none;
}

.select-css option
{
  font-weight: normal;
}

.cursor-pointer
{
  cursor: var(--pointer);
}

.icon-cell
{
  width: var(--em-8);
}

.ellipsis
{
  width: 90% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.pointer-events-none
{
  pointer-events: none;
}

.original-file
{
  opacity: 0;
  position: absolute;
  top: -1000em;
  left: -1000em;
}

.composite-image-exporting .composite-image
{
  transform: none !important;
  transition: none !important;
  box-shadow: none !important;
  border: 0 !important;
  position: static !important;
  width: 100% !important;
  height: 100% !important;
  overflow: visible !important;
}

.composite-image-exporting .error,
.composite-image-exporting .social-bar,
.composite-image-exporting .controls-area-wrapper
{
  display: none;
}

.composite-image-exporting .composite-image,
.composite-image-exporting .composite-image img
{
  position: absolute !important;
  top: 0 !important;;
  left: 0 !important;;
  z-index: 1000;
  width: 100% !important;
  height: auto !important;
}

.composite-image-exporting *
{
  transition: none !important;
}

.composite-image-exporting body::before
{
  font: normal var(--em-6) Helvetica, Sans-serif;
  line-height: 100vh;
  text-align: center;
  content: attr(data-exporting);
  display: block;
  background: var(--darker);
  width: 100%;
  height: 100%;
  position: fixed !important;
  top: 0;
  left: 0;
  z-index: 2000;
  opacity: .75;
}

.css-hdr-tour .v-step
{
  background: var(--green) !important;
  filter: drop-shadow(0 0 var(--em-1) var(--darker)) !important;
  border-radius: var(--border-radius) !important;
  padding: var(--em-2) !important;
  color: var(--white) !important;
}

.css-hdr-tour .v-step[x-placement="top"] .v-step__arrow
{
  border-top-color: var(--green) !important;
}

.css-hdr-tour .v-step[x-placement="left"] .v-step__arrow
{
  border-left-color: var(--green) !important;
}

.css-hdr-tour .v-step[x-placement="bottom"] .v-step__arrow
{
  border-bottom-color: var(--green) !important;
}

.css-hdr-tour .v-step[x-placement="right"] .v-step__arrow
{
  border-right-color: var(--green) !important;
}

.css-hdr-tour .v-step__content
{
  font-size: var(--em-3) !important;
}

.css-hdr-tour .v-step__button
{
  text-transform: uppercase !important;
  font-size: var(--em-3) !important;
  vertical-align: middle !important;
  width: 80px !important;
}

::-webkit-scrollbar
{
  height: var(--em-3);
  width: var(--em-3);
  background: var(--darker);
  border-radius: var(--em-3);
}

::-webkit-scrollbar-corner,
::-webkit-scrollbar-thumb
{
  background: rgba(0, 0, 0, .75);
}

::-webkit-scrollbar-thumb
{
  border-radius: var(--em-2);
}
</style>



// WEBPACK FOOTER //
// src/App.vue